import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "./CheckoutForm";
import { useCsrfToken } from "../../hooks/useCsrfToken";
import { baseAxios, defaultAxios } from "../../apis";
import { useAuth } from "../../context/authContext";
import CardSubscription from "../cards/CardSubscription";
import { IconBasicSubs, IconFreeSubs, IconProSubs } from "../../assets/icons";
import Dialog from "../dialogs";
import DialogSuccess from "../dialogs/DialogSuccess";
import { useSidebar } from "../../context/sidebarContext";
import CardSubscriptionSkeleton from "../cards/skeletons/CardSubscriptionSkeleton";
import { extractErrorMessage } from "../../utils";
import { useToast } from "../../context/toastContext";
import useAuthedApi from "../../hooks/useAuthedApi";
import { useNavigate, useSearchParams } from "react-router-dom";
import DialogAsk from "../dialogs/DialogAsk";

// const stripePromise = loadStripe(
//   "pk_test_51SQP5nJKKvQIyg6av83jzgdNyxit1dP4fKcvgPmcQRacXGBi8UO26bvCnkX0lAsRrwfgFHZarn6nSYzh08nKnprT00OODQjFbw"
// );
const stripePromise = loadStripe(
  "pk_live_51MJT5MLvYVnHTDMECD4wFxots0wHsWGfaT01g9nhaxb2KQk4gvuw6nwOe8Kv0y7A2lWVrgVEhtORcWjyPil9enwo00CBVkkH0k"
);

// --- HOOKS ---

// Hook to fetch all initial subscription and plan data
const useSubscriptionDataQuery = (user, isLanding) => {
  return useQuery({
    // Add isLanding to queryKey to ensure caching separates landing vs logged-in data
    queryKey: ["subscriptionData", user?.stripe_customer_id, isLanding],
    queryFn: async () => {
      let plansRes;
      let currentSubscription = null;

      // 1. Fetch Plans based on isLanding prop
      if (isLanding) {
        // Use defaultAxios and specific read-all endpoint for landing
        plansRes = await defaultAxios.get("/api/plans/read-all");
      } else {
        // Use baseAxios and standard endpoint for logged in users
        plansRes = await baseAxios.get("/api/plans/");
      }

      // 2. Fetch User Subscription (Only if NOT landing and user exists)
      if (!isLanding && user?.stripe_customer_id) {
        try {
          const subRes = await baseAxios.get(
            `/api/get-subscription/${user.stripe_customer_id}`,
          );
          currentSubscription = subRes.data;
        } catch (error) {
          console.error("Failed to fetch user subscription:", error);
          currentSubscription = null;
        }
      }

      // This part of the code will now run even if the subscription fetch failed
      const sortedPlans = (plansRes.data || []).sort((a, b) => {
        if (a.product_name.includes("Free Trial")) return -1;
        if (b.product_name.includes("Free Trial")) return 1;
        if (a.product_name.includes("Standard")) return -1;
        if (b.product_name.includes("Standard")) return 1;
        return 0;
      });

      // The query will now succeed, returning all plans and a null subscription
      return { plans: sortedPlans, currentSubscription };
    },
    // Enable if it is a landing page OR if the user is loaded
    enabled: isLanding || !!user,
  });
};
// Mutation hook for creating a Stripe Setup Intent
const useCreateSetupIntentMutation = ({ setClientSecret, setShowCheckout }) => {
  const { authedPost } = useAuthedApi(); // Get the authedPost function
  const { addToast } = useToast();

  return useMutation({
    mutationFn: async (customerId) => {
      // The mutation function is now cleaner
      const response = await authedPost("/api/create-setup-intent/", {
        customerId,
      });
      return response.data.clientSecret;
    },
    onSuccess: (clientSecret) => {
      setClientSecret(clientSecret);
      setShowCheckout(true);
    },
    onError: (err) => {
      const errorMessage = extractErrorMessage(err?.response?.data || err);
      addToast("error", errorMessage, 10000);
    },
  });
};

// Mutation hook for canceling a subscription
const useCancelSubscriptionMutation = ({ setOpenDialog }) => {
  const { authedPost } = useAuthedApi(); // Get the authedPost function
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriptionId) => {
      // The mutation function is now cleaner
      const response = await authedPost(
        `/api/subscriptions/${subscriptionId}/cancel/`,
        {}, // Empty payload
      );
      return response.data;
    },
    onSuccess: () => {
      setOpenDialog("success-cancel-subs");
      queryClient.invalidateQueries({ queryKey: ["subscriptionData"] });
    },
    onError: (err) => {
      const errorMessage = extractErrorMessage(err?.response?.data || err);
      addToast("error", errorMessage, 10000);
    },
  });
};

const useCreateCheckoutSessionMutation = () => {
  const { authedPost } = useAuthedApi();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: async ({ customerId, priceId }) => {
      const response = await authedPost("/api/subscribe-with-payment-method/", {
        customerId,
        priceId,
      });
      return response.data; // response.data seharusnya berisi { url: "..." }
    },
    onSuccess: (data) => {
      if (data.url) {
        // Ini adalah langkah kuncinya: redirect ke halaman Stripe
        window.location.href = data.url;
      } else {
        addToast("error", "Failed to get checkout URL.", 10000);
      }
    },
    onError: (err) => {
      const errorMessage = extractErrorMessage(err);
      addToast("error", errorMessage, 10000);
    },
  });
};

export const SubInfoPage = ({
  className,
  classNameContent,
  isDialog,
  isLanding,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setIsOpenUpgradeDialog } = useSidebar();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // UI state is kept
  const [isYearly, setIsYearly] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [pendingPriceId, setPendingPriceId] = useState(null);
  const [openDialog, setOpenDialog] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  // Call the new hooks to manage data and actions

  const { data, isLoading, error } = useSubscriptionDataQuery(user, isLanding);
  const { plans = [], currentSubscription } = data || {};

  const createSetupIntentMutation = useCreateSetupIntentMutation({
    setClientSecret,
    setShowCheckout,
  });
  const createCheckoutSessionMutation = useCreateCheckoutSessionMutation();
  const cancelSubscriptionMutation = useCancelSubscriptionMutation({
    setOpenDialog,
  });

  const selectedPlan = plans.find((p) => p.price_id === pendingPriceId) || null;

  // Handler functions are now simple wrappers for the mutations
  const handleSubscribe = (priceId) => {
    if (!user?.stripe_customer_id || createSetupIntentMutation.isPending)
      return;
    setPendingPriceId(priceId);
    createSetupIntentMutation.mutate(user.stripe_customer_id);
  };

  // Handler subscribe disederhanakan untuk memanggil mutasi baru
  // const handleSubscribe = (priceId) => {
  //   if (!user?.stripe_customer_id || createCheckoutSessionMutation.isPending) {
  //     return;
  //   }
  //   createCheckoutSessionMutation.mutate({
  //     customerId: user.stripe_customer_id,
  //     priceId: priceId,
  //   });
  // };

  const handleCancel = (subscriptionId) => {
    if (!subscriptionId || cancelSubscriptionMutation.isPending) return;
    cancelSubscriptionMutation.mutate(subscriptionId);
  };

  const isFreeOrTrialPlan = (plan) => {
    const name = plan.product_name?.toLowerCase() || "";

    const isTrial = name.includes("trial");

    const isFreePlan = name === "free" || name.includes("free plan");

    const isZeroPrice = plan.unit_amount === 0;

    return isTrial || isFreePlan || isZeroPrice;
  };

  useEffect(() => {
    // Ambil nilai dari parameter URL 'subscription'
    const subscriptionStatus = searchParams.get("subscription");

    // Jika ada parameter 'subscription' atau 'session_id'
    if (subscriptionStatus) {
      // Tampilkan notifikasi toast yang sesuai
      if (subscriptionStatus === "cancelled") {
        // addToast("info", "Your subscription has been cancelled.");
        setOpenDialog("cancel-subscribe");
      } else if (subscriptionStatus === "success") {
        queryClient.invalidateQueries({ queryKey: ["subscriptionData"] });
        setOpenDialog("success-subscribe");
      }
    }
  }, [queryClient, searchParams]);

  const generateFeaturesFromMetadata = (plan) => {
    // The "Free" plan has custom descriptions that don't map 1:1 with its metadata.
    // It's easiest to handle this as a special case using its unique product ID.
    if (plan.product_id === "prod_TuS6EvOX9dk0Zx") {
      return ["1 Pair DCA", "1 Pair Rebalance", "Grid (Fixed parameters)"];
    }

    const metadata = plan.product_metadata;
    // If no metadata, return empty array to prevent errors
    if (!metadata) return [];

    const features = [];

    // Helper to safely parse metadata values to integers
    const getLimit = (key) => parseInt(metadata[key] || "0", 10);

    // --- Map each metadata key to a feature string ---

    // DCA Bots
    const dcaLimit = getLimit("dca_bot_limit");
    if (dcaLimit > 0) {
      features.push(`${dcaLimit} Xenorize DCA bots`);
    }

    // Grid Bots
    const gridLimit = getLimit("grid_bot_limit");
    if (gridLimit > 0) {
      let gridText = `${gridLimit} Xenorize Grid bots`;
      // Add special text for "Standard" (Advance) plans
      if (plan.product_name?.toLowerCase().includes("standard")) {
        gridText += " (1~2%)";
      }
      features.push(gridText);
    }

    // Rebalance Bots (combines two metadata fields)
    const rebalanceBotLimit = getLimit("rebalance_bot_limit");
    const rebalancePairLimit = getLimit("rebalance_pair_limit");
    if (rebalanceBotLimit > 0 && rebalancePairLimit > 0) {
      features.push(
        `${rebalancePairLimit} Pair ${rebalanceBotLimit} Bot Rebalance`,
      );
    }

    // V2 Bots
    const v2Limit = getLimit("V2_bot_limit");
    if (v2Limit > 0) {
      features.push(`${v2Limit} Xenoarc bots`);
    }

    // V3 Bots
    const v3Limit = getLimit("V3_bot_limit");
    if (v3Limit > 0) {
      // Note: Your old hardcoded value was "3 Pair V3". The metadata only provides a bot limit.
      // I am assuming the simpler "3 V3" is correct. Adjust if "Pair" is needed.
      features.push(`${v3Limit} V3`);
    }

    return features;
  };

  const renderPlan = (plan) => {
    const getPlanDetails = () => {
      const nameLower = plan?.product_name?.toLowerCase() || "";
      if (nameLower.includes("basic")) {
        return { icon: IconBasicSubs, highlight: true };
      }
      if (nameLower.includes("advance")) {
        return { icon: IconProSubs, highlight: false };
      }
      // Default to "Free"
      return { icon: IconFreeSubs, highlight: false };
    };

    const { icon, highlight } = getPlanDetails();

    //  Generate features dynamically from the plan's metadata
    const features = generateFeaturesFromMetadata(plan);

    const isCurrentPlan =
      currentSubscription?.items?.data?.[0]?.plan?.id === plan.price_id;

    const priceDisplay =
      // plan.product_id === "prod_SYxraiwTwCuhPz" // Free plan ID - STG
      // plan.product_id === "prod_TYN17v4ho1q1v1" // Free plan ID - PRD
      plan.product_id === "prod_TuS6EvOX9dk0Zx" // New Free plan ID - PRD
        ? "Free"
        : plan.unit_amount && plan.interval
          ? `$${(plan.unit_amount / 100).toFixed(2)}/${plan.interval}`
          : null;

    const expiryTimestamp = Number(
      currentSubscription?.items?.data?.[0]?.current_period_end,
    );

    return (
      <CardSubscription
        plan={plan}
        title={plan?.product_name}
        price={priceDisplay}
        originalPrice=""
        icon={icon}
        features={features} // Use the dynamically generated features
        buttonText={isLanding ? "Choose Plan" : "Get Started"}
        highlight={highlight}
        isCurrentPlan={isCurrentPlan}
        onClick={() => {
          if (isLanding) {
            navigate("/login");
            return;
          }
          handleSubscribe(plan.price_id);
        }}
        onCancel={() => setOpenDialog("ask-cancel")}
        expiryTimestamp={expiryTimestamp}
        currentSubscription={currentSubscription}
        // loadingCancel={cancelSubscriptionMutation.isPending}
      />
    );
  };

  return (
    <div className={`bg-white pt-2 ${className}`}>
      <DialogSuccess
        open={openDialog === "success-subscribe"}
        onClose={() => {
          setOpenDialog(null);
          setIsOpenUpgradeDialog(false);

          // Hapus parameter dari URL agar tidak muncul lagi saat refresh
          searchParams.delete("subscription");
          setSearchParams(searchParams, { replace: true });
        }}
        title={"Subscription successful – you’ve now unlocked premium access!"}
      />

      <DialogSuccess
        open={openDialog === "cancel-subscribe"}
        onClose={() => {
          setOpenDialog(null);
          setIsOpenUpgradeDialog(false);

          // Hapus parameter dari URL agar tidak muncul lagi saat refresh
          searchParams.delete("subscription");
          setSearchParams(searchParams, { replace: true });
        }}
        status="error"
        title={"Payment process cancelled. You have not been charged."}
      />

      <DialogSuccess
        open={openDialog === "success-cancel-subs"}
        onClose={() => {
          setOpenDialog(null);
          setIsOpenUpgradeDialog(false);
        }}
        title={
          "Cancel Subscription successful – Subscription will be cancelled at the end of the current period."
        }
      />

      <DialogAsk
        open={openDialog === "ask-cancel"}
        onClose={() => setOpenDialog(null)}
        title={
          "Are you sure you wish to proceed with the cancellation of this plan?"
        }
        onConfirm={() => handleCancel(currentSubscription?.id)}
        loading={cancelSubscriptionMutation.isPending}
      />

      <h2 className="!text-2xl md:!text-[32px] font-bold text-center mt-2 text-gray-900 !leading-[1.4]">
        Give your trading an edge beyond manual clicks
        {/* <br className="hidden xl:block" /> */}
        🪙
      </h2>
      <span className="block text-[#21272A] text-center text-sm font-light mt-4 mb-6 leading-[1.8]">
        Select a plan that lets advanced bots work alongside your manual trading
        — tightening your risk control, reducing mistakes,{" "}
        <br className="hidden xl:block" />
        and keeping your strategy running even when you’re away.
      </span>

      <div className="mb-2 p-2 bg-gray-100 rounded-lg flex m-4 mx-auto border w-full max-w-[800px]">
        <div
          className={`flex-1 flex justify-center items-center p-2.5 rounded-md text-sm font-semibold transition-all duration-150 ease-in-out focus:outline-none cursor-pointer text-center ${
            !isYearly
              ? "bg-white text-gray-900 shadow-custom-dual"
              : "text-gray-500 hover:bg-gray-200/70 hover:text-gray-700"
          }`}
          onClick={() => setIsYearly(false)}
        >
          Pay Monthly
        </div>
        <div
          className={`flex max-xl:flex-col justify-center items-center flex-1 gap-1 xl:gap-2 p-2.5 rounded-md text-sm font-semibold transition-all duration-150 ease-in-out focus:outline-none cursor-pointer text-center ${
            isYearly
              ? "bg-white text-gray-900 shadow-custom-dual"
              : "text-gray-500 hover:bg-gray-200/70 hover:text-gray-700"
          }`}
          onClick={() => setIsYearly(true)}
        >
          Pay Annually{" "}
          <span className="bg-[#007AFF] text-xs p-2 py-1.5 text-white rounded-[10px]">
            20% OFF
          </span>
        </div>
      </div>

      {isLoading ? (
        <div
          className={`grid grid-cols-1 xl:flex justify-center max-xl:items-start gap-4 mt-10 xl:mt-20 xl:w-full xl:max-w-[65rem] xl:mx-auto ${classNameContent}`}
        >
          {[...Array(3)].map((_, idx) => (
            <CardSubscriptionSkeleton key={idx + "skeleton"} />
          ))}
        </div>
      ) : (error || !user?.stripe_customer_id) && !isLanding ? (
        <p className="text-center text-red-500 mt-8">
          Failed to load subscription plans. Please try again later.
        </p>
      ) : (
        <div
          className={`grid grid-cols-1 xl:flex justify-center max-xl:items-start gap-4 mt-10 xl:mt-20 xl:w-full xl:max-w-[65rem] xl:mx-auto ${classNameContent}`}
        >
          {(() => {
            const filteredPlans = plans
              .filter((plan, index, self) => {
                const FREE_PRODUCT_ID = "prod_TuS6EvOX9dk0Zx";
                const currentProductId =
                  currentSubscription?.items?.data?.[0]?.plan?.product;

                const trialStart = currentSubscription?.trial_start;
                const trialEnd = currentSubscription?.trial_end;

                const isFreePlan = plan.product_id === FREE_PRODUCT_ID;
                const isUserOnFreePlan = currentProductId === FREE_PRODUCT_ID;
                const isInTrial = trialStart != null && trialEnd != null;

                //  Hide free plan if not current and trial is over
                if (isFreePlan && !isUserOnFreePlan && !isInTrial) return false;

                //  Only filter interval for non-free plans
                if (
                  !isFreePlan &&
                  plan.interval !== (isYearly ? "year" : "month")
                ) {
                  return false;
                }

                //  Deduplicate by price_id
                const isFirstOccurrence =
                  index === self.findIndex((p) => p.price_id === plan.price_id);

                return isFirstOccurrence;
              })
              .sort((a, b) => {
                // Sort order: Free (if visible) -> Basic -> Advance
                const FREE_PRODUCT_ID = "prod_TuS6EvOX9dk0Zx";

                if (a.product_id === FREE_PRODUCT_ID) return -1;
                if (b.product_id === FREE_PRODUCT_ID) return 1;

                const aIsAdvance = a.product_name
                  ?.toLowerCase()
                  .includes("advance");
                const bIsAdvance = b.product_name
                  ?.toLowerCase()
                  .includes("advance");

                // Basic should come first (return -1), Advance should come last (return 1)
                if (aIsAdvance && !bIsAdvance) return 1;
                if (!aIsAdvance && bIsAdvance) return -1;

                return 0;
              })
              .filter((sub) => !isFreeOrTrialPlan(sub));

            if (filteredPlans.length === 0) {
              return (
                <div className="w-full text-center py-12">
                  <p className="text-gray-500 text-lg">
                    No {isYearly ? "yearly" : "monthly"} subscription plans
                    available at the moment.
                  </p>
                </div>
              );
            }

            return filteredPlans.map((plan) => renderPlan(plan));
          })()}
        </div>
      )}

      <Dialog
        open={showCheckout && clientSecret}
        onClose={() => {
          if (subscribing) return;
          setShowCheckout(false);
        }}
        width="450px"
        className="!bg-[#FFFFFF] !ring-1 !ring-[#D9D9D9]"
      >
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            appearance: {
              theme: "stripe",
              rules: {
                ".TermsText": {
                  fontSize: "14px",
                  lineHeight: "1.5",
                  color: "#6B7280",
                },
              },
            },
          }}
        >
          <CheckoutForm
            onClose={() => {
              // if (subscribing) return;
              setShowCheckout(false);
            }}
            plan={selectedPlan}
            customerId={user?.stripe_customer_id}
            priceId={pendingPriceId}
            setLoadingSubscribe={setSubscribing}
            setOpenDialog={setOpenDialog}
          />
        </Elements>
      </Dialog>

      {!isDialog && !isLanding && (
        <div className="flex flex-col items-center mt-12 mb-14">
          {/* <span className="block text-center">
            After you click Upgrade, you will direct to <strong>Stripe</strong>{" "}
            Payment Gateway
          </span> */}
          <span className="text-center text-[rgba(27,31,38,0.72)] block mt-1">
            Need Help? Contact{" "}
            <a
              // href="mailto:support@xenorize.com"
              href="https://xenorize.freshdesk.com/support/tickets/new"
              target="_blank"
              className="underline text-[#007AFF] font-light"
            >
              support@xenorize.com
            </a>
          </span>
        </div>
      )}
    </div>
  );
};
